import type { TranslationKey } from "@privacy/translations";
import { useState } from "react";
import type { FC } from "react";
import { Translate, useTranslation } from "@privacy/translations";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { IconButton } from "@mui/material";
import { TrashCanIcon, PersonIcon2, PersonIcon1 } from "../../../shared/icons";
import type { RegistrationById, RegistrationPermission, UserWithAccess } from "../../../api/registrations.types";
import { usePermission } from "../../../shared/permissions";
import { Drawer } from "../../../shared/Drawer";
import { usePrivacyNavigate } from "../../../shared/usePrivacyNavigate";
import {
  useDeleteInvitee,
  useGetRegistrationById,
  useGetRegistrationUsersWithAccess,
} from "../../../api/registrations";
import { ConfirmDeleteDialog } from "../../../shared/ConfirmDeleteDialog";
import { DynamicGrid } from "../../../dynamicGrid";
import { AddInviteeButton } from "./Invitee/AddInvitee";
import { RegistrationDrawerHeader } from "./RegistrationDrawerHeader";
import { useRegistrationVariant } from "../RegistrationVariantProvider";

export const UsersWithAccessSubDrawer = () => {
  const registrationId = DynamicGrid.useSelectedRowId();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const variant = useRegistrationVariant();
  const { data: registration } = useGetRegistrationById(registrationId, variant);
  const { data: usersWithAccess } = useGetRegistrationUsersWithAccess(registrationId, variant);
  const { navigate } = usePrivacyNavigate();

  return (
    <DynamicGrid.Drawer key={registrationId}>
      {(() => {
        if (!registration || !usersWithAccess) return <Drawer.Loading />;

        return (
          <Stack sx={{ height: "100%" }}>
            <RegistrationDrawerHeader
              activeTab="standard"
              statusIndicator="hidden"
              registration={registration}
              subDrawerHeader={
                <Drawer.SubDrawerHeader
                  count={usersWithAccess.usersWithPermissions.length}
                  onBackClick={() => navigate("..")}
                  renderIcon={props => <PersonIcon1 {...props} />}
                  title={t("RegistrationDrawer.UsersWithAccessSubDrawer.Header")}
                >
                  <Stack sx={{ ml: "auto" }}>
                    <AddInviteeButton
                      disabled={!hasPermission("nova-forms-api:registration:invite", registration.permissions)}
                      registrationId={registration.id}
                      userIdsToExclude={[
                        ...registration.invitees.map(invite => invite.userId),
                        ...(registration.responsible ? [registration.responsible.userId] : []),
                      ]}
                    />
                  </Stack>
                </Drawer.SubDrawerHeader>
              }
            />
            <UserWithAccessGrid usersWithAccess={usersWithAccess.usersWithPermissions} registration={registration} />
          </Stack>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

/** From lowest to highest weight */
const allColumnPermissions = ["READ", "INVITE", "IS_INVITEE", "EDIT", "REVIEW"] as const;

const columnPermissionWeights = Object.fromEntries(
  allColumnPermissions.map((permission, index) => [
    permission,
    /** This is used to make them sum of lower permission weights to always be less than a higher weight */
    Math.pow(2, index),
  ])
);

/** This is a permission only used in this grid and does not correspond one to one with the real permissions*/
type ColumnPermission = (typeof allColumnPermissions)[number];

type UserWithAccessRow = {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  columnPermissions: ColumnPermission[];
  columnPermissionLabel: string;
  totalColumnPermissionWeight: number;
};

const getUserWithAccessToRow = (
  userWithAccess: UserWithAccess,
  t: (key: TranslationKey) => string
): UserWithAccessRow => {
  /** ⚠️ This feature does not map well with our backend permissions. And therefore has to make
   * some dangerous assumptions */

  const mapToColumnPermission: Record<RegistrationPermission, ColumnPermission> = {
    "nova-forms-api:registration:review": "REVIEW",

    "nova-forms-api:registration:invite": "INVITE",
    "nova-forms-api:registration:update": "EDIT",
    "nova-forms-api:registration:read": "READ",

    /** Unused in this grid */
    "nova-forms-api:registration:create": "READ",
    "nova-forms-api:registration:delete": "READ",
    "nova-forms-api:registration:answers:update": "READ",
  };

  let columnPermissions = userWithAccess.registrationPermissions.map(permission => mapToColumnPermission[permission]);

  /** "Edit" overrides "Edit (guest)" */
  if (!columnPermissions.includes("EDIT") && userWithAccess.isInvitee) columnPermissions.push("IS_INVITEE");

  /** Both "Edit" and "Edit (guest)" overrides "Read". */
  if (columnPermissions.includes("EDIT") || columnPermissions.includes("IS_INVITEE")) {
    columnPermissions = columnPermissions.filter(permission => permission !== "READ");
  }

  /** Remove duplicates */
  columnPermissions = Array.from(new Set(columnPermissions));

  /** Sort from highest to lowest weight */
  columnPermissions.sort((z, a) => columnPermissionWeights[a] - columnPermissionWeights[z]);

  const columnPermissionLabel = uppercaseFirstLetter(
    columnPermissions
      .map(permission => t(`RegistrationDrawer.UsersWithAccessSubDrawer.ColumnPermission.${permission}`))
      .join(", ")
  );

  const totalColumnPermissionWeight = columnPermissions.reduce(
    (total, permission) => total + columnPermissionWeights[permission],
    0
  );

  return { ...userWithAccess, columnPermissions, columnPermissionLabel, totalColumnPermissionWeight };
};
const uppercaseFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

const PERMISSION_FIELD = "permission";

const UserWithAccessGrid: FC<{ usersWithAccess: UserWithAccess[]; registration: RegistrationById }> = ({
  usersWithAccess,
  registration,
}) => {
  const { t } = useTranslation();

  return (
    <Drawer.Grid
      showColumnHeaders
      getRowId={row => row.userId}
      rows={usersWithAccess.map(user => getUserWithAccessToRow(user, t))}
      slots={{ toolbar: Drawer.GridToolbar, noRowsOverlay: NoRowsOverlay }}
      initialState={{ sorting: { sortModel: [{ field: PERMISSION_FIELD, sort: "asc" }] } }}
      columns={[
        {
          field: "user",
          width: 275,
          headerName: t("RegistrationDrawer.UsersWithAccessSubDrawer.GridPerson"),
          valueGetter: (_, { firstName, lastName, email }) => `${firstName}${lastName}${email}`,
          renderCell: ({ row }) => <UserCell row={row} />,
        },
        {
          field: PERMISSION_FIELD,
          flex: 1,
          headerName: t("RegistrationDrawer.UsersWithAccessSubDrawer.GridPermissions"),
          sortComparator: (a, z) =>
            UGLY_HACK.extractWeightFromValueString(z) - UGLY_HACK.extractWeightFromValueString(a),
          valueGetter: (_, row) => UGLY_HACK.buildValueString(row),
          renderCell: ({ row }) => <PermissionsCell row={row} registration={registration} />,
        },
      ]}
    />
  );
};

/** Ugly hack: The issue is that MUI base both the search and the sort on the value returned from "valueGetter".
 * And, we want to use the label("columnPermissionLabel") when searching, but the permission weight("totalColumnPermissionWeight)"
 * when sorting. Might exist a better solution that I can't find now */
const UGLY_HACK = {
  PERMISSION_FIELD_VALUE_SEPARATOR: "|",
  buildValueString: (row: UserWithAccessRow) =>
    `${row.totalColumnPermissionWeight}${UGLY_HACK.PERMISSION_FIELD_VALUE_SEPARATOR}${row.columnPermissionLabel}`,
  extractWeightFromValueString: (value: string) => parseInt(value.split(UGLY_HACK.PERMISSION_FIELD_VALUE_SEPARATOR)[0]),
};

const UserCell: FC<{ row: UserWithAccessRow }> = ({ row }) => {
  const { firstName, lastName, email } = row;
  return (
    <Drawer.GridCell>
      <Stack direction="row" sx={{ gap: 2 }}>
        <PersonIcon2 size="sm" />
        <Stack>
          <Typography
            variant="body2"
            sx={{ fontWeight: "600", lineHeight: 1.2, overflowWrap: "anywhere", hyphens: "auto" }}
          >
            {firstName} {lastName}
          </Typography>
          <Typography
            sx={{
              fontSize: theme => theme.typography.pxToRem(12),
              lineHeight: 1.2,
              overflowWrap: "anywhere",
              hyphens: "auto",
              color: theme => theme.palette.text.secondary,
            }}
          >
            {email}
          </Typography>
        </Stack>
      </Stack>
    </Drawer.GridCell>
  );
};

const PermissionsCell: FC<{ row: UserWithAccessRow; registration: RegistrationById }> = ({ row, registration }) => {
  const { hasPermission } = usePermission();

  const showDeleteInvitee =
    row.columnPermissions.includes("IS_INVITEE") &&
    hasPermission("nova-forms-api:registration:invite", registration.permissions);
  return (
    <Drawer.GridCell>
      <Stack direction="row">
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ lineHeight: 1.2, overflowWrap: "anywhere", hyphens: "auto" }} variant="body2">
            {row.columnPermissionLabel}
          </Typography>
        </Box>

        {showDeleteInvitee && <RemoveInvitee row={row} registration={registration} />}
      </Stack>
    </Drawer.GridCell>
  );
};

const RemoveInvitee: FC<{ row: UserWithAccessRow; registration: RegistrationById }> = ({ row, registration }) => {
  const { t } = useTranslation();
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const { mutateAsync: deleteInvitee } = useDeleteInvitee(registration.id);

  const handleClose = () => setIsDeleteConfirmOpen(false);

  const handleDeleteConfirm = () => deleteInvitee(row.userId, { onSuccess: handleClose });

  return (
    <>
      <IconButton
        aria-label={t("RegistrationDrawer.Invitees.DeleteLabel")}
        size="small"
        sx={{ ml: "auto", flexShrink: 0, mr: 2 }}
        onClick={() => setIsDeleteConfirmOpen(true)}
      >
        <TrashCanIcon size={14} />
      </IconButton>
      {isDeleteConfirmOpen && (
        <ConfirmDeleteDialog
          isOpen
          onDelete={handleDeleteConfirm}
          onClose={handleClose}
          headerText={t("DeleteInvitee.Dialog.Header")}
          message={
            <Translate
              translationKey="DeleteInvitee.Dialog.Message"
              values={{
                guestName: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {row.firstName} {row.lastName}
                  </Box>
                ),
                registrationName: (
                  <Box component="span" sx={{ fontWeight: "bold" }}>
                    {registration.name}
                  </Box>
                ),
              }}
            />
          }
          confirmButtonLabel={t("DeleteInvitee.Dialog.Button.Confirm")}
          id="confirm-delete"
        />
      )}
    </>
  );
};

const NoRowsOverlay = () => {
  return <Drawer.GridNoRowsOverlay labelKey="RegistrationDrawer.UsersWithAccessSubDrawer.GridEmpty" />;
};
